import { connect } from 'react-redux';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { sortBy } from 'lodash';
import i18next from 'i18next';
import React, { useEffect, useState, useRef } from 'react';

// Selectors
import { getSalesPointStores } from '@selectors/stores';

// Commons
import { DATAGRID_LOCALES } from '@commons/utils/dataGrid';
import { getTheme } from '@commons/utils/theme';
import {
  parseQuantityInput,
  isValidNumberInput,
  formatQuantityForDisplay,
} from '@commons/utils/numberInput';
import { TRANSFER_DETAILS_VIEW } from '@stocks/StocksInventories/Transfers/utils/constants';
import EmptyState from '@commons/EmptyState';
import Text, { ENUM_FONTS } from '@commons/Text';

import transferUtils from '../../../utils/index';

// Utils
import {
  DATAGRID_COMMON_PROPS,
  DATAGRID_SLOT_PROPS,
  getSupplierProductsDatagridColumns,
} from '../utils/getDatagridsConfig';
import {
  DataGridContainer,
  DecrementButton,
  getDatagridStyle,
  GridContainer,
  HorizontalButtonContainer,
  IncrementButton,
  PackagingContainer,
  PackagingIndicator,
  PackagingItem,
  PackagingName,
  PackagingNameWrapper,
  QuantityDisplay,
  QuantityInputInline,
} from './styledComponents';

const SupplierProductsDatagrid = (props) => {
  const {
    supplierProductsToDisplay,
    setSupplierProductsToDisplay,
    transferList,
    transferDetailsView,
    currency,
    user,
    userSalesPointStores,
    newlyAddedReferenceId,
  } = props;

  const theme = getTheme();
  const dataGridRef = useRef(null);

  const [focusedInput, setFocusedInput] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    sku: false,
    supplierName: false,
    isIngredientStrategic: false,
  });

  const [sortModel, setSortModel] = useState([{ field: 'name', sort: 'asc' }]);

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  const handlePackagingSelection = (supplierProductId, packagingId) => {
    const updatedSupplierProducts = supplierProductsToDisplay.map((supplierProduct) => {
      if (supplierProduct.id !== supplierProductId) {
        return supplierProduct;
      }

      return {
        ...supplierProduct,
        selectedPackagingId: packagingId,
      };
    });

    setSupplierProductsToDisplay(updatedSupplierProducts);
  };

  const handleValueChange = (supplierProductId, packagingId, newQuantity, rawValue = null) => {
    const updatedSupplierProducts = supplierProductsToDisplay.map((supplierProduct) => {
      if (supplierProduct.id !== supplierProductId) {
        return supplierProduct;
      }

      const quantityToUpdate = transferUtils.getTransferQuantityToUpdate(transferDetailsView);

      const updatedPackagings = supplierProduct.packagings.map((packaging) =>
        packaging.id === packagingId
          ? {
              ...packaging,
              selectedQuantity: newQuantity,
              selectedQuantityRaw: formatQuantityForDisplay(rawValue) || newQuantity.toString(), // raw string
              [quantityToUpdate]: newQuantity,
            }
          : packaging,
      );

      return {
        ...supplierProduct,
        packagings: updatedPackagings,
      };
    });

    setSupplierProductsToDisplay(updatedSupplierProducts);
  };

  const handleInputFocus = (supplierProductId, packagingId, isDisabled) => {
    if (isDisabled) {
      return;
    }

    setFocusedInput(`${supplierProductId}-${packagingId}`);
    handlePackagingSelection(supplierProductId, packagingId);
  };

  const handleInputBlur = () => {
    setFocusedInput(null);
  };

  const renderPackagingCell = (supplierProduct, isDisabled) => (
    <PackagingContainer>
      {supplierProduct.packagings.slice(0, 3).map((packaging) => {
        const isSelected = packaging.id === supplierProduct.selectedPackagingId;
        const isEmpty = !packaging.selectedQuantity;
        const inputKey = `${supplierProduct.id}-${packaging.id}`;
        const isFocused = focusedInput === inputKey;
        const isHighlighted = transferUtils.hasDifferentQuantities(packaging, transferDetailsView);

        return (
          <PackagingItem
            isDisabled={isDisabled}
            isSelected={isSelected}
            key={packaging.id}
            onClick={() => handleInputFocus(supplierProduct.id, packaging.id, isDisabled)}
          >
            {!isDisabled && <PackagingIndicator isSelected={isSelected} />}

            {!isDisabled && isFocused ? (
              <QuantityInputInline
                min="0"
                step="0.1"
                type="text"
                value={packaging.selectedQuantityRaw || packaging.selectedQuantity}
                autoFocus
                onBlur={handleInputBlur}
                onChange={(event) => {
                  const inputValue = event.target.value;

                  if (!isValidNumberInput(inputValue)) {
                    return;
                  }

                  const parsedValue = parseQuantityInput(inputValue);

                  handleValueChange(supplierProduct.id, packaging.id, parsedValue, inputValue);
                }}
                onKeyDown={(e) => {
                  // Prevent losing focus when using arrow keys
                  e.stopPropagation();
                }}
              />
            ) : (
              <QuantityDisplay
                isDisabled={isDisabled}
                isEmpty={isEmpty}
                isHighlighted={isHighlighted}
              >
                {isEmpty ? '—' : packaging.selectedQuantityRaw || packaging.selectedQuantity}
              </QuantityDisplay>
            )}
            <PackagingNameWrapper>
              <PackagingName isDisabled={isDisabled}>{packaging.name}</PackagingName>
            </PackagingNameWrapper>
          </PackagingItem>
        );
      })}
    </PackagingContainer>
  );

  const handleIncrementOrDecrement = (supplierProduct, packaging, quantity) => {
    const newQuantity = quantity < 0 ? 0 : quantity;
    const newRawValue = newQuantity.toString();
    handleValueChange(supplierProduct.id, packaging.id, newQuantity, newRawValue);
  };

  const renderIncrementButtons = (supplierProduct) => {
    const selectedPackaging = supplierProduct.packagings.find(
      ({ id }) => id === supplierProduct.selectedPackagingId,
    );

    if (!selectedPackaging) {
      return;
    }

    const isDisabled = [
      TRANSFER_DETAILS_VIEW.SENDER_DISABLED,
      TRANSFER_DETAILS_VIEW.DISABLED,
    ].includes(transferDetailsView);

    return (
      <HorizontalButtonContainer>
        <DecrementButton
          disabled={isDisabled || selectedPackaging.selectedQuantity <= 0}
          size="small"
          onClick={() =>
            handleIncrementOrDecrement(
              supplierProduct,
              selectedPackaging,
              selectedPackaging.selectedQuantity - 1,
            )
          }
        >
          <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>−</Text>
        </DecrementButton>
        <IncrementButton
          disabled={isDisabled}
          size="small"
          onClick={() =>
            handleIncrementOrDecrement(
              supplierProduct,
              selectedPackaging,
              selectedPackaging.selectedQuantity + 1,
            )
          }
        >
          <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>+</Text>
        </IncrementButton>
      </HorizontalButtonContainer>
    );
  };

  // Auto-scroll to newly added reference
  useEffect(() => {
    if (!newlyAddedReferenceId) {
      return;
    }

    const sortedSupplierProductsToDisplay = sortBy(supplierProductsToDisplay, 'name');
    const rowIndex = sortedSupplierProductsToDisplay.findIndex(
      ({ id }) => id === newlyAddedReferenceId,
    );

    if (rowIndex !== -1) {
      setTimeout(() => {
        // Calculate the page where the row is
        const targetPage = Math.floor(rowIndex / paginationModel.pageSize);
        if (targetPage !== paginationModel.page) {
          setPaginationModel({ ...paginationModel, page: targetPage });
        }

        // Wait for the pagination to update and then scroll to the row
        setTimeout(() => {
          const scrollContainer = dataGridRef.current?.querySelector(
            '.MuiDataGrid-virtualScroller',
          );

          if (scrollContainer) {
            const rowElements = scrollContainer.querySelectorAll('.MuiDataGrid-row');
            const targetRowInPage = rowIndex % paginationModel.pageSize;
            if (rowElements[targetRowInPage]) {
              rowElements[targetRowInPage].scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }
        }, 100);
      }, 100);
    }
  }, [newlyAddedReferenceId, paginationModel.pageSize, paginationModel.page]);

  // Prevent diagonal scroll on tablet
  useEffect(() => {
    let isScrolling = false;
    let scrollDirection = null;
    let touchStartX = 0;
    let touchStartY = 0;

    // Handle diagonal scroll on tablet
    const handleTouchStart = (e) => {
      const scrollContainer = e.target.closest('.MuiDataGrid-virtualScroller');
      if (!scrollContainer) {
        return;
      }

      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isScrolling = false;
      scrollDirection = null;
    };

    const handleTouchMove = (e) => {
      const scrollContainer = e.target.closest('.MuiDataGrid-virtualScroller');
      if (!scrollContainer) {
        return;
      }

      const touchX = e.touches[0].clientX;
      const touchY = e.touches[0].clientY;
      const deltaX = Math.abs(touchX - touchStartX);
      const deltaY = Math.abs(touchY - touchStartY);

      // Determine the main scroll direction
      if (!isScrolling && (deltaX > 10 || deltaY > 10)) {
        isScrolling = true;
        scrollDirection = deltaX > deltaY ? 'horizontal' : 'vertical';
      }

      // Prevent scroll in the non-main direction
      if (isScrolling && scrollDirection) {
        if (scrollDirection === 'horizontal' && deltaY > deltaX * 0.3) {
          e.preventDefault();
        } else if (scrollDirection === 'vertical' && deltaX > deltaY * 0.3) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = () => {
      isScrolling = false;
      scrollDirection = null;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const columns = getSupplierProductsDatagridColumns({
    renderPackagingCell,
    renderIncrementButtons,
    currency,
    transferList,
    transferDetailsView,
    isUserAssociatedToRecipient: userSalesPointStores.some(
      (store) => store.id === transferList.recipientStoreId,
    ),
    subCategoryOptions: [
      ...new Set(
        supplierProductsToDisplay.map(
          ({ subCategory }) => subCategory || i18next.t('GENERAL.NONE_VALUE'),
        ),
      ),
    ],
    supplierNameOptions: [
      ...new Set(
        supplierProductsToDisplay.map(
          ({ lnkSupplierSupplierproductrel }) =>
            lnkSupplierSupplierproductrel?.name || i18next.t('GENERAL.NONE_VALUE'),
        ),
      ),
    ],
  });

  return (
    <DataGridContainer>
      <GridContainer ref={dataGridRef}>
        <DataGridPro
          columns={columns}
          localeText={
            DATAGRID_LOCALES[user.lnkLanguageAccountrel.code].components.MuiDataGrid.defaultProps
              .localeText
          }
          pageSizeOptions={[50, 100]}
          paginationModel={paginationModel}
          rows={supplierProductsToDisplay}
          slots={{
            noRowsOverlay: () => (
              <EmptyState
                backgroundColor={theme.colors.greys.lightest}
                icon={'/images/inpulse/shopping.svg'}
                iconWidth="160px"
                label={i18next.t(
                  transferDetailsView === TRANSFER_DETAILS_VIEW.DRAFT
                    ? 'STOCKS.TRANSFERS.SUPPLIER_PRODUCTS_DATAGRID_EMPTY_STATE_TITLE'
                    : 'STOCKS.TRANSFERS.DATAGRID_EMPTY_STATE_TITLE',
                )}
              />
            ),
            ...DATAGRID_SLOT_PROPS,
          }}
          sortModel={sortModel}
          sx={getDatagridStyle(theme, newlyAddedReferenceId)}
          /*
           * This prop has a high impact on performances but it's the only way we found
           * to prevent the datagrid from randomly scrolling when clicking on an increment/decrement button
           */
          disableVirtualization
          {...DATAGRID_COMMON_PROPS}
          columnVisibilityModel={columnVisibilityModel}
          pagination
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          onPaginationModelChange={handlePaginationModelChange}
          onSortModelChange={handleSortModelChange}
        />
      </GridContainer>
    </DataGridContainer>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  currency: state.baseReducer.currency,
  userSalesPointStores: getSalesPointStores(state.baseReducer.activeStores),
});

export default connect(mapStateToProps)(SupplierProductsDatagrid);
